import type { TechnicalDocument, TechnicalDocumentRevision } from "../../../domain/documentation/technical-document.js";

export interface TechnicalDocumentRepository {
  save(document: TechnicalDocument): Promise<void>;
  findById(id: string): Promise<TechnicalDocument | null>;
  listAll(): Promise<TechnicalDocument[]>;
  saveRevision(revision: TechnicalDocumentRevision): Promise<void>;
  listRevisions(documentId: string): Promise<TechnicalDocumentRevision[]>;
}

export class InMemoryTechnicalDocumentRepository implements TechnicalDocumentRepository {
  private documents = new Map<string, TechnicalDocument>();
  private revisions = new Map<string, TechnicalDocumentRevision[]>();

  constructor() {
    try {
      if (typeof localStorage !== "undefined") {
        const docs = localStorage.getItem("inspect_technical_documents");
        if (docs) this.documents = new Map(JSON.parse(docs));
        const revs = localStorage.getItem("inspect_technical_document_revisions");
        if (revs) this.revisions = new Map(JSON.parse(revs));
      }
    } catch (e) {
      console.warn("Failed to load technical documents from localStorage", e);
    }
  }

  private persist(): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("inspect_technical_documents", JSON.stringify(Array.from(this.documents.entries())));
        localStorage.setItem("inspect_technical_document_revisions", JSON.stringify(Array.from(this.revisions.entries())));
      }
    } catch (e) {
      console.warn("Failed to persist technical documents to localStorage", e);
    }
  }

  async save(document: TechnicalDocument): Promise<void> {
    this.documents.set(document.id, document);
    this.persist();
  }

  async findById(id: string): Promise<TechnicalDocument | null> {
    return this.documents.get(id) ?? null;
  }

  async listAll(): Promise<TechnicalDocument[]> {
    return Array.from(this.documents.values());
  }

  async saveRevision(revision: TechnicalDocumentRevision): Promise<void> {
    const list = this.revisions.get(revision.documentId) || [];
    // Nunca sobrescreve: uma revisão já persistida (mesmo id) não é regravada com outro conteúdo.
    if (list.some(r => r.id === revision.id)) {
      throw new Error(`Revisão ${revision.id} já existe — versionamento não sobrescreve revisão anterior.`);
    }
    list.push(revision);
    this.revisions.set(revision.documentId, list);
    this.persist();
  }

  async listRevisions(documentId: string): Promise<TechnicalDocumentRevision[]> {
    return [...(this.revisions.get(documentId) || [])];
  }
}
